import React from 'react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import { CheckCircle, Award, Shield, Leaf } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { useLanguage } from '../../i18n/LanguageContext';
import { qualityFeatures as qualityFeaturesData, certifications } from '../../data/content';

const iconMap: Record<string, any> = { CheckCircle, Award, Shield, Leaf };

const Quality = () => {
  const { t, pick, path } = useLanguage();

  const qualityFeatures = qualityFeaturesData.map((f: any) => ({ ...f, icon: iconMap[f.icon] }));

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1.5rem'
  };

  const headerStyle = {
    textAlign: 'center' as const,
    marginBottom: '4rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '48rem',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    marginBottom: '4rem'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    transition: 'all 0.3s ease'
  };

  const iconStyle = {
    width: '3rem',
    height: '3rem',
    color: '#dfa926',
    marginBottom: '1.5rem'
  };

  const cardTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '1rem'
  };

  const cardDescStyle = {
    color: '#9ca3af',
    marginBottom: '1.5rem',
    lineHeight: '1.6'
  };

  const certGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem'
  };

  const certCardStyle = {
    background: 'rgba(223, 169, 38, 0.1)',
    border: '1px solid rgba(223, 169, 38, 0.3)',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    textAlign: 'center' as const
  };

  const certTitleStyle = {
    color: '#dfa926',
    fontWeight: '600',
    marginBottom: '0.5rem'
  };

  const certDescStyle = {
    color: '#d1d5db',
    fontSize: '0.875rem'
  };

  return (
    <div style={pageStyle}>
      <SEO
        title={t('seo.quality.title')}
        description={t('seo.quality.description')}
        canonicalPath="/quality"
        keywords={t('seo.quality.keywords')}
      />
      <Navigation />
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            {t('quality.titlePre')} <span style={highlightStyle}>{t('quality.titleHl')}</span>
          </h1>
          <p style={descriptionStyle}>
            {t('quality.desc')}
          </p>
        </div>

        <div style={gridStyle}>
          {qualityFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={index}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconComponent style={iconStyle} />
                <h3 style={cardTitleStyle}>{pick(feature, 'title')}</h3>
                <p style={cardDescStyle}>{pick(feature, 'description')}</p>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {pick(feature, 'details').map((detail: string, detailIndex: number) => (
                    <li key={detailIndex} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                      color: '#d1d5db',
                      fontSize: '0.875rem'
                    }}>
                      <span style={{
                        width: '0.5rem',
                        height: '0.5rem',
                        backgroundColor: '#dfa926',
                        borderRadius: '50%',
                        marginRight: '0.75rem'
                      }}></span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: '4rem' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: 'white',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            {t('quality.certTitlePre')} <span style={highlightStyle}>{t('quality.certTitleHl')}</span>
          </h2>

          <div style={certGridStyle}>
            {certifications.map((cert, index) => (
              <div key={index} style={certCardStyle}>
                <h4 style={certTitleStyle}>{cert.name}</h4>
                <p style={certDescStyle}>{pick(cert, 'description')}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(223, 169, 38, 0.1) 0%, rgba(223, 169, 38, 0.05) 100%)',
            padding: '3rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(223, 169, 38, 0.2)'
          }}>
            <h3 style={{ color: 'white', fontSize: '2rem', marginBottom: '1rem' }}>
              {t('quality.guarTitle')}
            </h3>
            <p style={{ color: '#d1d5db', marginBottom: '2rem', fontSize: '1.125rem' }}>
              {t('quality.guarDesc')}
            </p>
            <Link
              to={path('/contact')}
              style={{
                backgroundColor: '#dfa926',
                color: 'black',
                padding: '0.75rem 2rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#c8941f'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#dfa926'}
            >
              {t('quality.learnMore')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Quality;
